import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, CheckCircle2, XCircle, Loader2, ImageOff } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

// Lists every active product whose photo was uploaded by staff (URL contains
// "/api/uploads/") or is missing, and lets the admin batch-regenerate them
// with Gemini Nano Banana. Sequential calls so we never hammer the LLM.
export default function EnhancePhotosDialog({ open, onClose, onDone }) {
  const [items, setItems] = useState([]);          // candidates
  const [loading, setLoading] = useState(false);
  const [statusById, setStatusById] = useState({}); // {pid: "pending"|"working"|"done"|"error"}
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/manual-photos");
      setItems(data.items || []);
      setStatusById(Object.fromEntries((data.items || []).map((p) => [p.id, "pending"])));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const enhanceOne = async (p) => {
    setStatusById((s) => ({ ...s, [p.id]: "working" }));
    try {
      const { data } = await api.post(`/admin/products/${p.id}/enhance-image`, null, { timeout: 120000 });
      setStatusById((s) => ({ ...s, [p.id]: "done" }));
      // Patch the local item so the new thumb shows immediately
      setItems((arr) => arr.map((x) => x.id === p.id ? { ...x, image_url: data.new_url } : x));
      return true;
    } catch (e) {
      setStatusById((s) => ({ ...s, [p.id]: "error" }));
      toast.error(`${p.name}: ${formatApiError(e)}`);
      return false;
    }
  };

  const enhanceAll = async () => {
    setRunning(true);
    let okCount = 0;
    for (const p of items) {
      // Skip ones already done
      if (statusById[p.id] === "done") continue;
      const ok = await enhanceOne(p);
      if (ok) okCount++;
    }
    setRunning(false);
    toast.success(`Enhanced ${okCount} / ${items.length} photos`);
    if (onDone) onDone();
  };

  const counts = items.reduce((acc, p) => {
    acc[statusById[p.id] || "pending"] = (acc[statusById[p.id] || "pending"] || 0) + 1;
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !running && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="enhance-photos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <Sparkles className="h-5 w-5 text-[#E67E22]" />
            AI Enhance — Manually-uploaded photos
          </DialogTitle>
          <p className="text-xs text-[#738380]">
            Replaces staff-uploaded photos with studio-quality AI-generated photos via Gemini Nano Banana
            (with Arbe watermark). Each photo takes ~10s. <b>This cannot be undone</b> — the old URL is overwritten.
          </p>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 py-2 border-y border-[#E1E5DF] text-xs">
          <div className="flex items-center gap-3 text-[#4A5D5A]">
            <span><b className="text-[#1C2826]">{items.length}</b> candidates</span>
            {counts.done > 0 && <span className="text-[#2F5233]">✓ {counts.done} done</span>}
            {counts.error > 0 && <span className="text-[#C62828]">✗ {counts.error} failed</span>}
            {counts.working > 0 && <span className="text-[#E67E22]">⟳ {counts.working} working</span>}
          </div>
          <Button
            onClick={enhanceAll}
            disabled={loading || running || items.length === 0}
            className="h-9 bg-[#2F5233] hover:bg-[#1F3D24] text-white"
            data-testid="enhance-all-btn"
          >
            {running ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Enhancing…</> : <><Sparkles className="h-4 w-4 mr-1" /> Enhance all</>}
          </Button>
        </div>

        <div className="overflow-y-auto flex-1 -mx-2 px-2">
          {loading && <div className="text-center text-[#738380] py-10">Loading candidates…</div>}
          {!loading && items.length === 0 && (
            <div className="text-center text-[#738380] py-10 text-sm">
              No staff-uploaded photos found. All product images already use the brand-managed assets.
            </div>
          )}
          {items.map((p) => {
            const st = statusById[p.id] || "pending";
            return (
              <div key={p.id} className="flex items-center gap-3 py-2 border-b border-[#F1F3EE] last:border-b-0">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="h-12 w-12 rounded object-cover bg-[#F1F3EE] flex-shrink-0" data-testid={`enhance-thumb-${p.id}`} />
                ) : (
                  <div className="h-12 w-12 rounded bg-[#F1F3EE] flex items-center justify-center flex-shrink-0">
                    <ImageOff className="h-5 w-5 text-[#A0AAA7]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#1C2826] line-clamp-1">{p.name}</div>
                  <div className="text-[11px] text-[#738380]">{p.category}</div>
                </div>
                <div className="flex-shrink-0 w-24 text-right">
                  {st === "pending" && <span className="text-[11px] text-[#738380]">Pending</span>}
                  {st === "working" && <span className="text-[11px] text-[#E67E22] inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Working</span>}
                  {st === "done" && <span className="text-[11px] text-[#2F5233] inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Done</span>}
                  {st === "error" && <span className="text-[11px] text-[#C62828] inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> Failed</span>}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => enhanceOne(p)}
                  disabled={running || st === "working"}
                  data-testid={`enhance-one-${p.id}`}
                  className="h-8 text-xs"
                >
                  {st === "done" ? "Redo" : "Enhance"}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="pt-3 border-t border-[#E1E5DF] flex justify-end">
          <Button variant="outline" onClick={onClose} disabled={running} data-testid="enhance-close-btn">
            <X className="h-4 w-4 mr-1" /> {counts.done > 0 ? "Done" : "Close"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
